import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import i18n from 'scoreboard/components/i18n'
import enums from 'scoreboard/components/enums'
import globals from 'scoreboard/components/globals'
import groupType from 'scoreboard/admin/principals/groups/group-type'
import principalItemsTemplate from '../common/principal-items.hbs'
import tagTemplate from '../common/tags.hbs'
import expendituresTemplate from './expenditures.hbs'
import { formatItem } from 'scoreboard/related-items/related-items-list'
import {
	shortenedAndUnshortened
} from 'scoreboard/initiatives/format-initiative-calculations'
import {
	isColumnTypeUsingPeriodRangedForRepeatBy,
	scoringColorTypeForColumnType
} from './initiative-report-columns'
import reportNoteFormatter
	from 'scoreboard/reports/common/designer-note-formatter'
import escapePreserveLineBreaks
	from 'scoreboard/components/escape-preserve-line-breaks'
import ReportDataExtractor, {
	formattedRelatedItemHtml,
	toAbbreviatedResult
} from 'scoreboard/reports/common/report-data-extractor'
import isNumericDataType from 'scoreboard/components/is-numeric-data-type'
import currency from 'scoreboard/components/currency'
import reportConstants from 'scoreboard/reports/report-constants'
import isDateTimeDataType from 'scoreboard/components/is-date-time-data-type'
import DataType from 'scoreboard/datasets/model/data-type'
import unwrapModel from '../../components/unwrap-model'
import { formattedOverallColorHtml } from '../common/report-data-extractor'
const { DATE, CURRENCY } = DataType
import { applyColorStyles } from 'scoreboard/components/colored-cell-formatter'

function getValue(datum, columnType, defaultValue = '') {
	let property = columnTypeToResultProperty(columnType)
	return _.get(datum[property], 'value', defaultValue)
}

function applyScoringColor(result, resultItem, columnType, periodId = globals.periodId) {
	let scoringColorType = scoringColorTypeForColumnType(columnType)
	if (_.isNil(scoringColorType)) {
		return
	}

	let colorMap =  resultItem.overallColor
	if (scoringColorType == 'schedule-color') {
		colorMap = resultItem.scheduleColor
	} else if (scoringColorType == 'budget-color') {
		colorMap = resultItem.budgetColor
	}
	// #57149
	// Default to gray if the color is not found
	let color = colorMap.value[periodId] || constants?.scoreColors?.gray?.color
	applyColorStyles(result, color)
}

function columnTypeToResultProperty(type) {
	switch (type) {
		case 'currency' : return 'currencyCode'
		default: return _.camelCase(type)
	}
}

class InitiativeReportDataExtractor extends ReportDataExtractor {

	resolveContentAttributes(resultItem, columnModel) {
		let currencyCode = _.get(resultItem.currencyCode, 'value', null)
		let attributes = _.defaults({currencyCode}, columnModel.attributes)
		delete attributes.coercedDataType
		return attributes
	}

	/**
	 * Returns the HTML to be displayed to the end user for a particular
	 * formatted value. Does not include the exportValue and rawValue
	 * @return {String}
	 */
	_formattedValueHtml(value, contentAttributes) {
		let { columnType } = unwrapModel(contentAttributes)
		switch (columnType) {
			case 'overall-color':
				return formattedOverallColorHtml(value)
			case 'initiative-type':
				return formattedInitiativeTypeHtml(value)
			case 'archive-status':
				return formattedArchiveStatusHtml(value)
			case 'goal-function':
				return formattedGoalFunctionHtml(value)
			case 'related-items':
				return formattedRelatedItemHtml(value)
			default:
				return value
		}
	}

	/**
	 * Returns an object with all the information related to a formatted value
	 * (i.e. an object with these properties: html, rawValue, exportValue)
	 * @return {{exportValue, rawValue, html: String}
	 */
	_formatValue(value, contentAttributes) {
		let { columnType, dataType, currencyCode } = contentAttributes

		/*
		Any export formatting for an unknown data type must be passed through
		this function.
		 */
		const formatForExport = (fmtFn) => {
			// #44477 Datetime values should never be exported with any formatting.
			// Raw value will be formatted in Excel with the format string we provide.
			// #50991 but we do still need to return the unformatted date value
			if (isDateTimeDataType(dataType)) {
				return value
			} else {
				return fmtFn()
			}
		}

		if (columnType == 'expenditures' && value != null) {
			let formattedExpenditures = _.map(value, expenditure => {
				return {
					label: escapePreserveLineBreaks(expenditure.label),
					rawLabel: expenditure.label,
					date: this.applyValueFormatting(expenditure.date,
						contentAttributes, {dataType: DATE}),
					rawDate: expenditure.date,
					amount: this.applyValueFormatting(expenditure.amount,
						contentAttributes, {dataType: CURRENCY}),
					rawValue: expenditure.amount
				}
			})
			return {
				html: expendituresTemplate({value: formattedExpenditures}),
				exportValue: this._valuesToExportString(formattedExpenditures,
					v => `${v.rawLabel} ${v.rawDate}: ${v.rawValue}`)
			}
		}

		if (columnType == 'goal-function') {
			return { html: formattedGoalFunctionHtml(value) }
		}

		if (columnType == 'assignees') {
			groupType.groupTypeLabelAssignTo(value)
			return  {
				html: principalItemsTemplate({value}),
				exportValue: this._valuesToExportString(value, v => {
					return v.groupTypeLabel ?
						`${v.label} (${v.groupTypeLabel})` : v.label
				})
			}
		}

		if (columnType == 'archive-status') {
			return {
				html: this._formattedValueHtml(value, contentAttributes)
			}
		}

		if (columnType == 'related-items') {
			_.each(value, formatItem)
			return {
				// the related items are always a list, so that's why we call
				// _formatUniqueListedValues without any regard to the
				// aggregation type
				html: this._formatUniqueListedValues(value, contentAttributes),
				exportValue: this._formatExportRelatedItems(value)
			}
		}

		if (columnType == 'overall-color') {
			let html = value ?  this._formattedValueHtml(
				value[globals.periodId], contentAttributes) : null
			return { html }
		}

		if (columnType == 'currency') {
			return {
				html: currency.label(currencyCode)
			}
		}

		//handle if it's an initiative calculation type (except days elapsed
		//and percent time elapsed which used special rules #41140)
		if (columnType && _.result(
			constants.initiativeReportColumnTypes[columnType], 'changesOverTime'
		)) {
			//when not in a repeat-by we're grabbing the calculation for the current period
			let targetPeriod = this.reportModel.get('relativePeriodId')
			if (columnType == 'days-elapsed' || columnType == 'percent-time-elapsed') {
				//special case for these type which use's today's date
				targetPeriod = reportConstants.TODAYS_PSEUDO_PERIOD_ID
			}
			if (!_.isNil(value)) value = value[targetPeriod]
			return {
				html: _.isNil(value) ? '' :
					this._formatInitiativeCalculationValue(value, columnType,
					contentAttributes),
				exportValue: formatForExport(() => this._formatInitiativeCalculationValue(value, columnType,
					contentAttributes, { abbreviated: false })
				),
				rawValue: value ?? ''
			}
		}

		if (columnType == 'tags') {
			return  {
				html: tagTemplate({values: _.map(value, escapePreserveLineBreaks) })
			}
		}

		if (columnType === 'initiative-type') {
			// Use i18n to apply configurable terminology changes to the
			// initiative type #55589
			let configuredValue = i18n.get('initiatives.type.' + _.lowerCase(value))
			return {
				html: configuredValue
			}
		}

		//setup the formatting
		let exportValue = null
		let displayValue
		let rawValue
		if (dataType == 'text') {
			displayValue = escapePreserveLineBreaks(value || '')
		} else {
			displayValue = _.isNil(value) ? '' :
				this.applyValueFormatting(value, contentAttributes)
			exportValue = formatForExport(() =>
				this.applyValueFormatting(value, contentAttributes, {
					abbreviated: false
				})
			)
			rawValue = value
		}

		return { html: displayValue, exportValue, rawValue }
	}

	/**
	 * @param {Object[]} items
	 */
	_formatExportRelatedItems (items) {
		return this._valuesToExportString(items, v => v.label)
	}

	/**
	 * @override
	 */
	_formatAggregatedValue (value, contentAttributes) {
		let { columnType } = contentAttributes

		if (columnType === 'overall-color' && _.isArray(value)) {
			return value.map(
				v => this._formattedValueHtml(v, contentAttributes))
				.join(', ')
		}

		if (columnType == 'related-items' && _.isArray(value)) {
			return this._formatExportRelatedItems(value)
		}

		return null
	}

	extractData(result, resultItem, headerCell) {
		let { contentAttributes } = result
		let columnType = headerCell.columnType
		let value = getValue(resultItem, columnType, null)
		let isNewOverallColorColumn =
			!contentAttributes.colorType && columnType == 'overall-color'

		//apply any coloring
		// #57199
		// New overall color columns do not come with a colorType, so we need to
		// apply color based off the column type initially
		if (contentAttributes.colorType == 'scoring' ||
			contentAttributes.colorType == 'overall' ||
			isNewOverallColorColumn
		) {
			applyScoringColor(result, resultItem, columnType)
		}

		if (columnType == 'id') {
			result.html = _.toString(resultItem.id)
			return result
		}
		if (columnType == 'notes' && value != null) {
			result = reportNoteFormatter(result, headerCell.column.get('noteDisplayOptions').displayProperties, value, 'initiatives')
			return result
		}

		let {html, exportValue, rawValue} = this._formatValue(value, contentAttributes)
		result.html = html
		result.rawValue = rawValue

		this._storeExportModel(result, exportValue, contentAttributes)

		return result
	}

	/**
	 * Handles formatting initiative column values that change over time in a
	 * non aggregated period based repeat by (#39984)
	 */
	nonAggregatedPeriodBasedColumnDataContents(result, resultItem, headerCell) {
		let { breakdownColumnData } = headerCell
		let { breakdownItemId } = breakdownColumnData
		let { contentAttributes } = result
		let { columnType } = contentAttributes

		if (isColumnTypeUsingPeriodRangedForRepeatBy(columnType)) {
			let periodId = _.parseInt(breakdownItemId.firstBreakdownItemId)
			//the currency code on the initiative
			let currencyCode = _.get(resultItem.currencyCode, 'value', null)
			let extendedAttributes = _.extend({currencyCode}, contentAttributes)

			let valuesByPeriodId = getValue(resultItem, columnType, {})
			let value = valuesByPeriodId[periodId]
			result.html = this._formatInitiativeCalculationValue(value,
				columnType, extendedAttributes)
			this._storeExportModel(result, value, extendedAttributes)
		} else {
			//#41289 if this column type doesn't use a period range to repeat
			//then just repeat the value for each period
			result = this.extractData(result, resultItem, _.defaults({columnType}, headerCell))
		}

		return result
	}

	toNonRepeatedAggregatedValue(result, resultItem, headerCell) {
		let {column, columnType} = headerCell
		let property = columnTypeToResultProperty(columnType)
		if (columnType == 'id') {
			//special case, due to the API the id property is not using a
			//ReportResultDatum to be backwards compatiable with the legacy reports
			//we'll get rid of this shim once we remove the legacy reports
			//the server will just store the aggregation in numberOfRecords property since they are functionally equivalent
			property = 'numberOfRecords'
		}
		return _.get(resultItem[property], `aggregationsByColumnClientId.${column.columnClientId()}`, '')
	}

	toNonBreakdownRangedDataContents(result, resultItem, headerCell) {
		let { breakdownColumnData } = headerCell
		let { breakdownItemId } = breakdownColumnData
		let { contentAttributes } = result
		let { columnType } = contentAttributes

		if (isColumnTypeUsingPeriodRangedForRepeatBy(columnType)) {
			let periodId = _.parseInt(breakdownItemId.firstBreakdownItemId)
			let value = getValue(resultItem, columnType, null)[periodId]
			result.html = this._formatInitiativeCalculationValue(value, columnType, contentAttributes)
			this._storeExportModel(result, value, contentAttributes)
		} else {
			result = this.extractData(result, resultItem, _.defaults({columnType}, headerCell))
		}
		return result
	}

	_formatInitiativeCalculationValue(value, columnType, contentAttributes, overrides = {}) {
		let resolvedAttributes = _.extend({}, contentAttributes, overrides)
		let { dataType, abbreviated } = resolvedAttributes
		if (!isNumericDataType(dataType)) {
			return this.applyValueFormatting(value, contentAttributes, overrides)
		}

		//special case for aggregated-budget, uses the type 'budget'
		let calcColumnType = columnType == 'aggregated-budget' ? 'budget' : columnType
		let {shortened, unshortened} = shortenedAndUnshortened(calcColumnType, resolvedAttributes)(value)
		if (abbreviated) {
			return toAbbreviatedResult(shortened, unshortened, resolvedAttributes)
		} else {
			return unshortened
		}
	}

	formatForGroupHeader(grouping, columnAttributes) {
		let { displayValue, currencyCode } = grouping
		if (columnAttributes.columnType === 'id') return _.toString(displayValue)
		return this._formatValue(displayValue, _.extend({ currencyCode }, columnAttributes)).html
	}
}


/**
 * Converts a boolean or String value to a boolean, where "true" is
 * considered true, and other strings are considered false.
 */
function toBoolean(value) {
	return value === 'true' || value === true
}

function formattedInitiativeTypeHtml(value) {
	return i18n.get(`initiatives.type.${value}`)
}

function formattedArchiveStatusHtml(value) {
	return toBoolean(value) ?
		i18n.get('report.initiativeReport.archived') :
		i18n.get('report.initiativeReport.notArchived')
}

function formattedGoalFunctionHtml(value) {
	return i18n.get(enums.goalFunction[value])
}

export default InitiativeReportDataExtractor
export { applyScoringColor, toBoolean, formattedInitiativeTypeHtml, formattedArchiveStatusHtml,
	formattedGoalFunctionHtml }

InitiativeReportDataExtractor.__filename__ = 'reports/initiative-report/initiative-report-data-extractor.js'
